import { ALL_FLOW_COMPONENT_TYPES } from "@repo/shared";
import type { ComponentUsageRow } from "@repo/shared/types";
import { useEffect } from "react";
import { useNavigate } from "react-router";
import { getFlowComponentLabel } from "@/components/flow/flow-component-types";
import { PageHeader } from "@/components/page-header";
import { getPdfComponentLabel } from "@/components/pdf-templates/pdf-component-labels";
import { Skeleton } from "@/components/ui/skeleton";
import {
  SortableTableHead,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useTableSort } from "@/hooks/use-table-sort";
import { useAdminAccess, useComponentUsage } from "@/lib/hooks/use-admin";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import type { ComponentType } from "@/lib/types/flow";
import { T } from "./page.i18n";

// Component types present in the flow registry — used to decide which label
// resolver (flow vs PDF) applies to a given usage row. Types present in both
// registries share names and near-identical labels; the flow label wins.
const flowTypes = new Set<string>(ALL_FLOW_COMPONENT_TYPES);

type DisplayRow = ComponentUsageRow & { label: string };
type SortKey = keyof Pick<DisplayRow, "label" | "flows" | "pdfTemplates">;

export default function ComponentUsagePage() {
  const navigate = useNavigate();
  const { isLoading: accessLoading, canViewAdmin: canView } = useAdminAccess();
  const { rows, error, isLoading: dataLoading } = useComponentUsage();
  const { userLanguage } = useUserLanguage();
  const t = useT(T);

  const isLoading = accessLoading || dataLoading;

  useEffect(() => {
    if (isLoading) return;
    if (!canView) {
      navigate("/", { replace: true });
    }
  }, [isLoading, canView, navigate]);

  const displayRows: DisplayRow[] = rows.map((row) => ({
    ...row,
    label: flowTypes.has(row.componentType)
      ? getFlowComponentLabel(row.componentType as ComponentType, userLanguage)
      : getPdfComponentLabel(row.componentType, userLanguage),
  }));
  const { sorted, toggleSort, directionFor } = useTableSort(displayRows, "label");

  if (isLoading || !canView) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const columns: { key: SortKey; label: string; numeric?: boolean }[] = [
    { key: "label", label: t.component },
    { key: "flows", label: t.flows, numeric: true },
    { key: "pdfTemplates", label: t.pdfTemplates, numeric: true },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={[{ label: t.admin, href: "/admin" }, { label: t.title }]}
        title={t.title}
        description={t.description}
      />

      {error ? (
        <p className="text-destructive text-center py-8">{t.loadError}</p>
      ) : sorted.length === 0 ? (
        <p className="text-muted-foreground text-center py-8">{t.noData}</p>
      ) : (
        <Table variant="data">
          <TableHeader>
            <TableRow>
              {columns.map((col) => (
                <SortableTableHead
                  key={col.key}
                  className={col.numeric ? "text-right" : undefined}
                  direction={directionFor(col.key)}
                  onSort={() => toggleSort(col.key)}
                >
                  {col.label}
                </SortableTableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((row) => (
              <TableRow key={row.componentType}>
                <TableCell className="font-medium">{row.label}</TableCell>
                <TableCell className="text-right font-mono">{row.flows ?? "—"}</TableCell>
                <TableCell className="text-right font-mono">{row.pdfTemplates ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
